import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FormButton from "../../components/form-components/FormButton.js";
import SelectedValuesChips from "../../components/ui-components/SelectedValuesChips.jsx";
import Toggle from "../../components/ui-components/Toggle.jsx";
import { toast } from "../../components/ui-components/Toast.jsx";
import { authenticatedPost } from "../../api/authenticatedFetch.js";
import {
  listSchedulesByType,
  updateSchedule,
  deleteSchedule,
  pauseSchedule,
  resumeSchedule,
  triggerSchedule,
} from "../data-imports/api.js";
import {
  buildCronFromForm,
  parseCronToForm,
  formatScheduleSummary,
  scheduleWeekdayOptions,
  validateRecipients,
} from "../reports/scheduleUtils.js";

// The scheduler job type the Go backend's board-report generator handler
// (Task 15) registers and reads `metadata.board_report_id` from - see
// backend_go internal/scheduler/config.go JobTypeBoardReportGeneration.
// Exported so the Settings tab can read the same schedule without duplicating
// the job-type string or the lookup below.
export const JOB_TYPE = "board_report_generation";

/**
 * BoardReportScheduleModal - lets a user create/update/pause/resume/delete a
 * cron delivery schedule for a single board report (email of the latest
 * APPROVED run's PDF). Mirrors the widget-report ScheduleManagerModal +
 * ScheduleEditorModal pair from src/views/reports/, collapsed into one modal
 * because a board report has at most one schedule (unlike widget reports,
 * which can have several named schedules) - see Task 16 brief.
 */
const extractRecipients = (schedule) => {
  const metadata = schedule?.metadata || {};
  const rawRecipients =
    schedule?.recipients ||
    schedule?.emails ||
    metadata.recipients ||
    metadata.email_recipients ||
    metadata.emails ||
    [];

  if (Array.isArray(rawRecipients)) {
    return rawRecipients.filter(Boolean);
  }

  if (typeof rawRecipients === "string") {
    return rawRecipients
      .split(/[,\s]+/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  return [];
};

export const findScheduleForReport = (schedules, boardReportId) => {
  return (
    (schedules || []).find((schedule) => {
      const metaId = schedule?.metadata?.board_report_id;
      return metaId != null && String(metaId) === String(boardReportId);
    }) || null
  );
};

const normalizeDefaultRecipients = (defaultRecipients) => {
  return (defaultRecipients || [])
    .map((r) => (typeof r === "string" ? r : r?.email))
    .filter(Boolean);
};

const BoardReportScheduleModal = ({ show, boardReportId, boardReportName, defaultRecipients, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [schedule, setSchedule] = useState(null);

  const [frequency, setFrequency] = useState("daily");
  const [weekdays, setWeekdays] = useState([]);
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [time, setTime] = useState("09:00");
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [status, setStatus] = useState("active");
  const [isAdvancedCron, setIsAdvancedCron] = useState(false);
  const [warning, setWarning] = useState("");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const modalStyle =
    "bg-black bg-opacity-50 z-[9999] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full cursor-default text-text";
  const containerStyle =
    "w-[640px] bg-card relative border-card-bd border rounded-card flex flex-col z-[10000] max-h-[80vh]";

  const resetForm = useCallback(() => {
    setFrequency("daily");
    setWeekdays([]);
    setDayOfMonth("1");
    setTime("09:00");
    setRecipients(normalizeDefaultRecipients(defaultRecipients));
    setRecipientInput("");
    setStatus("active");
    setIsAdvancedCron(false);
    setWarning("");
    setErrors({});
  }, [defaultRecipients]);

  const loadSchedule = useCallback(async () => {
    if (!boardReportId) {
      return;
    }
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await listSchedulesByType(JOB_TYPE);
      const list = response?.schedules || response?.data || response || [];
      const normalized = Array.isArray(list) ? list : [];
      const found = findScheduleForReport(normalized, boardReportId);
      setSchedule(found);

      if (!found) {
        resetForm();
        return;
      }

      const cron = found?.schedule_expression || found?.scheduleExpression || found?.cron || "";
      const parsed = parseCronToForm(cron);
      setRecipients(extractRecipients(found));
      setRecipientInput("");
      setStatus(found?.current_status === "paused" ? "paused" : "active");
      setErrors({});

      if (parsed.isAdvanced) {
        setIsAdvancedCron(true);
        setWarning("This schedule uses an advanced pattern. Editing is limited.");
        return;
      }

      setIsAdvancedCron(false);
      setWarning("");
      setFrequency(parsed.frequency || "daily");
      setWeekdays(parsed.weekdays || []);
      setDayOfMonth(parsed.dayOfMonth || "1");
      setTime(parsed.time || "09:00");
    } catch (error) {
      console.error("Failed to load board report schedule:", error);
      setLoadError("Couldn’t load schedule. Try again.");
    } finally {
      setIsLoading(false);
    }
  }, [boardReportId, resetForm]);

  useEffect(() => {
    if (show) {
      setDeleteConfirm(false);
      loadSchedule();
    }
  }, [show, loadSchedule]);

  const selectedWeekdays = useMemo(() => new Set(weekdays), [weekdays]);

  const handleToggleWeekday = (value) => {
    setWeekdays((prev) => (prev.includes(value) ? prev.filter((day) => day !== value) : [...prev, value]));
  };

  const collectRecipientTokens = useCallback(() => {
    if (!recipientInput.trim()) {
      return [];
    }
    return recipientInput
      .split(/[,\s]+/)
      .map((value) => value.trim())
      .filter(Boolean);
  }, [recipientInput]);

  const handleAddRecipients = useCallback(() => {
    const tokens = collectRecipientTokens();
    if (tokens.length === 0) {
      return;
    }
    setRecipients((prev) => Array.from(new Set([...prev, ...tokens])));
    setRecipientInput("");
  }, [collectRecipientTokens]);

  const handleRecipientKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddRecipients();
    }
  };

  const handleRemoveRecipient = (value) => {
    setRecipients((prev) => prev.filter((item) => item !== value));
  };

  const validateForm = (currentRecipients) => {
    const nextErrors = {};

    const validation = validateRecipients(currentRecipients);
    if (!validation.isValid) {
      nextErrors.recipients =
        validation.valid.length === 0 ? "Add at least one valid email." : "One or more recipients are invalid.";
    }

    if (frequency === "weekly" && weekdays.length === 0) {
      nextErrors.weekdays = "Select at least one weekday.";
    }

    if (frequency === "monthly") {
      const parsed = Number(dayOfMonth);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 31) {
        nextErrors.dayOfMonth = "Enter a day between 1 and 31.";
      }
    }

    if (!time) {
      nextErrors.time = "Select a time.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (isAdvancedCron) {
      return;
    }

    const pendingTokens = collectRecipientTokens();
    const mergedRecipients = Array.from(new Set([...recipients, ...pendingTokens]));
    if (pendingTokens.length > 0) {
      setRecipients(mergedRecipients);
      setRecipientInput("");
    }

    if (!validateForm(mergedRecipients)) {
      return;
    }

    const cron = buildCronFromForm({
      frequency,
      time,
      weekdays,
      dayOfMonth: frequency === "monthly" ? Number(dayOfMonth) : dayOfMonth,
    });

    const { valid } = validateRecipients(mergedRecipients);
    // Delivery actually reads recipients off the board report itself (see
    // GenerateScheduledBoardReport), not this metadata - it's carried here
    // to mirror the widget-report schedule's metadata/recipients convention
    // (ScheduleEditorModal) and for display in this modal's schedule list.
    const metadata = {
      board_report_id: String(boardReportId),
      recipients: valid,
      channel: "email",
      format: "pdf",
    };

    setIsSaving(true);
    setErrors((prev) => ({ ...prev, submit: undefined }));
    try {
      if (schedule) {
        const payload = {
          schedule_expression: cron,
          metadata,
          current_status: status,
        };
        await updateSchedule(schedule.id, payload);
        toast.success("Schedule updated");
      } else {
        const createPayload = {
          job_type: JOB_TYPE,
          job_id: String(boardReportId),
          schedule_expression: cron,
          metadata,
        };
        const response = await authenticatedPost("/api/v1/schedules", createPayload);
        const created = response?.schedule || response?.data || response;

        if (status === "paused" && created?.id) {
          try {
            await pauseSchedule(created.id);
          } catch (pauseErr) {
            console.error("Failed to pause schedule:", pauseErr);
            toast.error("Schedule created, but pausing it failed. It's currently active.");
            return;
          }
        }
        toast.success("Schedule created");
      }
      await loadSchedule();
    } catch (error) {
      console.error("Failed to save board report schedule:", error);
      setErrors((prev) => ({ ...prev, submit: "Couldn’t save schedule. Try again." }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAction = async (action) => {
    if (!schedule) {
      return;
    }
    setIsActing(true);
    try {
      if (action === "pause") {
        await pauseSchedule(schedule.id);
      } else if (action === "resume") {
        await resumeSchedule(schedule.id);
      } else if (action === "trigger") {
        await triggerSchedule(schedule.id);
        toast.success("Delivery triggered. It may take a moment to arrive.");
      } else if (action === "delete") {
        await deleteSchedule(schedule.id);
        setDeleteConfirm(false);
        toast.success("Schedule deleted");
        await loadSchedule();
        return;
      }
      await loadSchedule();
    } catch (error) {
      console.error(`Failed to ${action} schedule:`, error);
      toast.error("Couldn’t update schedule. Try again.");
    } finally {
      setIsActing(false);
    }
  };

  const handleClose = () => {
    setDeleteConfirm(false);
    onClose?.();
  };

  if (!show) {
    return null;
  }

  const isPaused = schedule?.current_status === "paused";
  const summary = schedule ? formatScheduleSummary(schedule?.schedule_expression || schedule?.cron || "") : "";

  return ReactDOM.createPortal(
    <div className={modalStyle}>
      <div className={containerStyle} onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-row justify-between items-start p-4 border-border border-b">
          <div className="flex flex-col ml-6 mt-2">
            <div className="text-text-3 uppercase text-sm">Schedule</div>
            <div className="text-text text-2xl font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {`Schedule for "${boardReportName || "board report"}"`}
            </div>
          </div>
          <button onClick={handleClose} aria-label="Close dialog" className="cursor-pointer">
            <FontAwesomeIcon
              icon={icon({ name: "xmark", family: "sharp", style: "solid" })}
              className="w-6 h-6 text-text-3 text-[20px] mr-2 mt-1"
            />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-surface-2 px-10 py-6">
          {isLoading && <div className="text-text-2 text-sm">Loading schedule…</div>}
          {loadError && <div className="text-danger text-sm mb-4">{loadError}</div>}
          {warning && (
            <div className="bg-warning-soft border border-warning text-warning text-sm rounded-ctrl px-3 py-2 mb-4">
              {warning}
            </div>
          )}

          {!isLoading && (
            <div className="flex flex-col gap-5">
              {schedule && (
                <div className="bg-card border border-card-bd rounded-card p-3 flex items-center justify-between">
                  <div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isPaused ? "bg-surface-2 text-text-2" : "bg-success-soft text-success"
                      }`}
                    >
                      {isPaused ? "Paused" : "Active"}
                    </span>
                    <div className="text-text-2 text-sm mt-1">{summary}</div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm text-text-2">Frequency</label>
                <div className={`mt-2 ${isAdvancedCron ? "pointer-events-none opacity-50" : ""}`}>
                  <Toggle
                    items={["daily", "weekly", "monthly"]}
                    value={frequency}
                    changeHandler={isAdvancedCron ? () => {} : setFrequency}
                    style="buttons"
                  />
                </div>
              </div>

              {frequency === "weekly" && (
                <div>
                  <label className="text-sm text-text-2">Weekdays</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {scheduleWeekdayOptions.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => handleToggleWeekday(day.value)}
                        className={`px-3 py-1 rounded-full text-sm cursor-pointer border ${
                          selectedWeekdays.has(day.value)
                            ? "bg-primary border-primary text-white"
                            : "border-border text-text-2"
                        }`}
                        disabled={isAdvancedCron}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {errors.weekdays && <div className="text-danger text-xs mt-1">{errors.weekdays}</div>}
                </div>
              )}

              {frequency === "monthly" && (
                <div>
                  <label htmlFor="board-report-schedule-day-of-month" className="text-sm text-text-2">
                    Day of month
                  </label>
                  <input
                    id="board-report-schedule-day-of-month"
                    type="number"
                    min="1"
                    max="31"
                    value={dayOfMonth}
                    onChange={(event) => setDayOfMonth(event.target.value)}
                    className="w-full mt-1 rounded-ctrl border border-border p-2 text-sm text-text"
                    disabled={isAdvancedCron}
                  />
                  {errors.dayOfMonth && <div className="text-danger text-xs mt-1">{errors.dayOfMonth}</div>}
                </div>
              )}

              <div>
                <label htmlFor="board-report-schedule-time" className="text-sm text-text-2 mr-2">
                  Time
                </label>
                <input
                  id="board-report-schedule-time"
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  className="w-full mt-1 rounded-ctrl border border-border p-2 text-sm text-text"
                  disabled={isAdvancedCron}
                />
                {errors.time && <div className="text-danger text-xs mt-1">{errors.time}</div>}
              </div>

              <div className="bg-card border border-card-bd rounded-card p-3">
                <div className="text-xs uppercase text-text-2">Delivery</div>
                <div className="text-sm text-text mt-1">Send via: Email</div>
                <div className="text-sm text-text">Format: PDF (latest approved run)</div>
              </div>

              <div>
                <label htmlFor="board-report-schedule-recipients" className="text-sm text-text-2">
                  Recipients
                </label>
                <div className="mt-1 border border-border rounded-ctrl p-2 bg-card">
                  <SelectedValuesChips values={recipients} onRemove={handleRemoveRecipient} />
                  <input
                    id="board-report-schedule-recipients"
                    type="text"
                    value={recipientInput}
                    onChange={(event) => setRecipientInput(event.target.value)}
                    onKeyDown={handleRecipientKeyDown}
                    onBlur={handleAddRecipients}
                    placeholder="Add email addresses"
                    className="w-full mt-2 outline-none text-sm text-text"
                    disabled={isAdvancedCron}
                  />
                </div>
                {errors.recipients && <div className="text-danger text-xs mt-1">{errors.recipients}</div>}
              </div>

              <div>
                <label className="text-sm text-text-2">Status</label>
                <div className="mt-2 flex gap-4">
                  {[
                    { value: "active", label: "Active" },
                    { value: "paused", label: "Paused" },
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm text-text">
                      <input
                        type="radio"
                        name="board-report-schedule-status"
                        value={option.value}
                        checked={status === option.value}
                        onChange={() => setStatus(option.value)}
                        disabled={isAdvancedCron}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              {errors.submit && <div className="text-danger text-sm">{errors.submit}</div>}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center gap-2 py-5 px-6 border-t border-border bg-card">
          <div className="flex items-center">
            <FormButton
              title={isSaving ? "Saving…" : "Save schedule"}
              style="primary"
              onClick={handleSave}
              disabled={isSaving || isAdvancedCron || isLoading}
            />
            <FormButton title="Cancel" onClick={handleClose} disabled={isSaving} />
          </div>
          {schedule && (
            <div className="flex items-center">
              {isPaused ? (
                <FormButton title="Resume" onClick={() => handleAction("resume")} disabled={isActing} />
              ) : (
                <FormButton title="Pause" onClick={() => handleAction("pause")} disabled={isActing} />
              )}
              <FormButton title="Trigger now" onClick={() => handleAction("trigger")} disabled={isActing} />
              {deleteConfirm ? (
                <>
                  <FormButton
                    title="Confirm delete"
                    style="danger"
                    onClick={() => handleAction("delete")}
                    disabled={isActing}
                  />
                  <FormButton title="Keep" onClick={() => setDeleteConfirm(false)} disabled={isActing} />
                </>
              ) : (
                <FormButton title="Delete" style="danger" onClick={() => setDeleteConfirm(true)} disabled={isActing} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

BoardReportScheduleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boardReportName: PropTypes.string,
  defaultRecipients: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ email: PropTypes.string, name: PropTypes.string })])
  ),
  onClose: PropTypes.func.isRequired,
};

export default BoardReportScheduleModal;
